<template>
  <div>
    <div
      v-if="
        innerUser.profil == PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL ||
        innerUser.profil == PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL
      "
    >
      <AutoCompleteWithValidation
        v-if="!readonly"
        v-model="region"
        group="PosteDetails"
        vid="region"
        :rules="`required|shouldMatchFollowing:${filteredRegions()}`"
        :label="$t('RegionSelection.region')"
        :data="filteredRegions()"
        :placeholder="$t('RegionSelection.entrer-region')"
        :empty-message="$t('RegionSelection.region-non-trouvee')"
        @select="changeUserRegion"
      />
      <b-field v-else :label="$t('RegionSelection.region')">
        <span v-html="user.grae ? user.grae.region : ''" />
      </b-field>
    </div>

    <InputWithValidation
      group="PosteDetails"
      vid="fonction"
      rules="required"
      :label="$t('profilePage.fonction')"
      :model-value="innerUser.fonction"
      :placeholder="$t('profilePage.saisir-fonction')"
      :disabled="readonly"
      @update:model-value="updateUserFonction"
      @manual-change="changeUserProfile"
    />

    <InputWithValidation
      group="PosteDetails"
      vid="employeur"
      rules="required"
      :label="$t('profilePage.employeur')"
      :model-value="innerUser.employeur"
      :placeholder="$t('profilePage.saisir-employeur')"
      :disabled="readonly"
      @update:model-value="updateUserEmployeur"
      @manual-change="changeUserProfile"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { GraeDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"

import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import AutoCompleteWithValidation from "@/views/inputs/AutoCompleteWithValidation.vue"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { BField } from "buefy"
import { UsersService } from "@/services/rest/users/UsersService"

export default defineComponent({
  name: "PosteDetails",
  components: {
    InputWithValidation,
    AutoCompleteWithValidation,
    BField,
  },
  props: {
    user: {
      type: Object as PropType<UtilisateurDTO>,
      required: true,
    },
    regionModifiable: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["change-poste"],
  data() {
    return {
      PROFIL_UTILISATEUR,
      infoReportingService: InfoReportingService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,

      innerUser: { ...this.user } as UtilisateurDTO,
      region: "",
      regions: [] as string[],
      allGraes: [] as GraeDTO[],
    }
  },
  mounted(): void {
    if (this.innerUser) {
      this.region = this.innerUser.grae ? this.innerUser.grae.region : ""
    }
    this.init()
  },
  methods: {
    filteredRegions(): string[] {
      return this.regions.filter((r) => r.toLowerCase().includes(this.region.toLowerCase()))
    },

    async init(): Promise<void> {
      try {
        this.allGraes = await this.organismeService.getAllGraes()
        this.regions = this.allGraes.map((g) => g.region)
      } catch (err) {
        this.infoReportingService.error(
          this.$t("RegionSelection.erreur-api").toString(),
          err as Error,
        )
      }
    },

    changeUserRegion(newRegion: string): void {
      const matchingGrae = this.allGraes.filter((grae) => grae.region == newRegion)
      if (matchingGrae.length) {
        // Copy user to avoid direct prop mutation
        this.innerUser.grae = matchingGrae[0]
      }
      this.changeUserProfile()
    },

    updateUserFonction(newValue: string): void {
      this.innerUser.fonction = newValue
      this.changeUserProfile()
    },

    updateUserEmployeur(newValue: string): void {
      this.innerUser.employeur = newValue
      this.changeUserProfile()
    },

    changeUserProfile(): void {
      if (
        !UsersService.INSTANCE.hasAnyProfile(this.innerUser, [
          PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
          PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
        ])
      ) {
        this.innerUser.grae = undefined
      }
      this.$emit("change-poste", this.innerUser)
    },
  },
})
</script>
